import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import env from '../config/env';

const Changelog = ({ open, onClose }) => {
  const [changelog, setChangelog] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      const fetchChangelog = async () => {
        try {
          setLoading(true);
          setError(null);

          // Try to fetch the changelog from the assets folder
          const response = await fetch('/assets/CHANGELOG.md');

          if (response.ok) {
            const text = await response.text();
            // Check if we got HTML instead of markdown (happens when file doesn't exist)
            if (text.trim().startsWith('<!doctype') || text.trim().startsWith('<html')) {
              throw new Error('Received HTML instead of markdown file');
            }
            setChangelog(text);
          } else {
            // If changelog file doesn't exist, show a default message
            setChangelog(`# Changelog

## Version ${env.version}

No changelog available yet.

---

This is where the application changelog will be displayed. The changelog file should be located at \`/public/assets/changelog.md\`.`);
          }
        } catch (err) {
          console.error('Error fetching changelog:', err);
          setError('Failed to load changelog');
          setChangelog(`# Changelog

## Version ${env.version}

Unable to load changelog file.

---

Error: ${err.message}`);
        } finally {
          setLoading(false);
        }
      };

      fetchChangelog();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" component="h1">
            Application Version & Changelog
          </Typography>
          <Chip
            label={`${env.version}`}
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              '& h1': { fontSize: '1.75rem', fontWeight: 600, mb: 2 },
              '& h2': { fontSize: '1.25rem', fontWeight: 500, mb: 1.5, mt: 2.5 },
              '& h3': { fontSize: '1.1rem', fontWeight: 500, mb: 1, mt: 2 },
              '& p': { mb: 1.5, lineHeight: 1.6 },
              '& ul': { mb: 1.5, pl: 3 },
              '& li': { mb: 0.5 },
              '& code': {
                backgroundColor: 'action.hover',
                padding: '2px 4px',
                borderRadius: 1,
                fontSize: '0.875rem'
              },
              '& pre': {
                backgroundColor: 'action.hover',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                fontSize: '0.875rem'
              }
            }}
          >
            <ReactMarkdown>
              {changelog}
            </ReactMarkdown>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Changelog;
